import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Plus, ArrowLeft, MapPin, Tag, Hash, Loader2, ImageIcon, Upload, X, MessageCircle, ChevronLeft, ChevronRight, Store } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { compressImage, waLink } from "../lib/img";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const EMPTY = { title: "", description: "", price: "", whatsapp: "", email: "", contact: "", advertiser: "", trade: "", site_url: "", whatsapp_updates_consent: false, products: [{ name: "", description: "", price: "" }], images: [] };

const CATEGORY_FIELDS = {
  cars: [{ key: "make", label: "الشركة المصنعة" }, { key: "model", label: "الطراز" }, { key: "year", label: "سنة الصنع" }, { key: "mileage", label: "الممشى" }, { key: "condition", label: "الحالة" }],
  nature: [{ key: "activity", label: "نوع النشاط / المحصول" }, { key: "area", label: "المساحة أو الكمية" }, { key: "season", label: "الموسم" }, { key: "delivery", label: "التوصيل" }],
  home: [{ key: "service_type", label: "نوع الخدمة" }, { key: "coverage", label: "الأحياء المخدومة" }, { key: "availability", label: "أوقات التوفر" }, { key: "warranty", label: "الضمان" }],
  furniture: [{ key: "item_type", label: "نوع الأثاث أو الكتاب" }, { key: "material", label: "الخامة" }, { key: "dimensions", label: "المقاسات" }, { key: "condition", label: "الحالة" }],
};

export default function Category() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [section, setSection] = useState("");
  const [ads, setAds] = useState([]);
  const [open, setOpen] = useState(sp.get("add") === "1");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [detail, setDetail] = useState(null);
  const fileRef = useRef(null);

  const cat = cats.find((c) => c.id === id);

  useEffect(() => {
    api.categories().then((d) => {
      setCats(d.categories || []);
      setCities(d.cities || []);
      if (d.cities?.[0]) setCity(d.cities[0].name);
    });
  }, []);

  const reload = () => api.listAds({ category: id, ...(city ? { city } : {}), ...(section ? { section } : {}) }).then(setAds).catch(() => setAds([]));
  useEffect(() => { reload(); }, [id, city, section]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 10 - form.images.length;
    if (room <= 0) { toast.error("الحد الأقصى 10 صور"); return; }
    setUploading(true);
    try {
      const picked = files.slice(0, room);
      const out = [];
      for (const f of picked) out.push(await compressImage(f));
      setForm((p) => ({ ...p, images: [...p.images, ...out] }));
      if (files.length > room) toast.info(`تمت إضافة ${room} صور فقط (الحد 10)`);
    } catch { toast.error("تعذّر معالجة الصورة"); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeImg = (i) => setForm((p) => ({ ...p, images: p.images.filter((_, x) => x !== i) }));
  const updateProduct = (index, key, value) => setForm((previous) => ({
    ...previous,
    products: previous.products.map((product, current) => current === index ? { ...product, [key]: value } : product),
  }));
  const addProduct = () => setForm((previous) => previous.products.length >= 20 ? previous : ({
    ...previous, products: [...previous.products, { name: "", description: "", price: "" }],
  }));
  const removeProduct = (index) => setForm((previous) => ({
    ...previous, products: previous.products.filter((_, current) => current !== index),
  }));

  const submit = async () => {
    if (!form.title || !form.description || !form.whatsapp || !form.email || !form.products.some((product) => product.name.trim())) {
      toast.error("أكمل بيانات المتجر والبريد وأضف منتجًا أو خدمة على الأقل");
      return;
    }
    if (!form.whatsapp_updates_consent) { toast.error("وافق على رسائل تحديث الطلب عبر واتساب"); return; }
    setLoading(true);
    try {
      const ad = await api.createAd({
        category: id, city: city || cities[0]?.name, section: section || cat?.sections?.[0],
        title: form.title, description: form.description, price: form.price,
        whatsapp: form.whatsapp, contact: form.contact, trade: form.trade,
        email: form.email, whatsapp_updates_consent: form.whatsapp_updates_consent,
        details: form.details || {},
        site_url: form.site_url, products: form.products.filter((product) => product.name.trim()),
        advertiser: form.advertiser || "معلن", images: form.images,
      });
      setPaymentOrder(ad);
      toast.success(`تم إنشاء طلب الدفع ${ad.reference}؛ الإعلان بانتظار الدفع والمراجعة`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر نشر الإعلان");
    }
    setLoading(false);
  };

  return (
    <main className="relative min-h-screen pt-28">
      <div className="absolute left-1/2 top-20 h-64 w-[700px] -translate-x-1/2 hero-glow blur-3xl" />
      <section className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white">
          <ArrowLeft className="h-4 w-4 rotate-180" /> الرئيسية
        </Link>

        <div className="mt-4 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="rounded-full border border-cyan/25 bg-cyan/5 px-3 py-1 text-xs font-medium text-cyan">قسم · 4 مدن × 13 قسم</span>
            <h1 className="mt-3 font-display text-4xl font-extrabold text-white">{cat?.name || "التصنيف"}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{cat?.desc}</p>
          </div>
          <Button data-testid="open-add-ad-btn" onClick={() => setOpen(true)} className="btn-cyan h-11 gap-2 rounded-xl px-5 font-semibold hover:bg-transparent">
            <Plus className="h-4 w-4" /> إضافة إعلان
          </Button>
        </div>

        {/* filters */}
        <div className="mt-8 space-y-4">
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> المنطقة / المدينة</div>
            <div className="flex flex-wrap gap-2">
              {cities.map((c) => (
                <button key={c.id} data-testid={`city-filter-${c.id}`} onClick={() => setCity(c.name)}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${city === c.name ? "btn-cyan" : "border border-white/10 text-muted-foreground hover:text-white"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-muted-foreground"><Tag className="h-3.5 w-3.5" /> القسم</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setSection("")}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${section === "" ? "bg-white text-[#0b0f0d]" : "border border-white/10 text-muted-foreground hover:text-white"}`}>الكل</button>
              {(cat?.sections || []).map((s) => (
                <button key={s} onClick={() => setSection(s)}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${section === s ? "bg-white text-[#0b0f0d]" : "border border-white/10 text-muted-foreground hover:text-white"}`}>{s}</button>
              ))}
            </div>
          </div>
        </div>

        {/* ads grid */}
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ads.map((a) => (
            <div key={a.id} data-testid={`ad-card-${a.id}`} onClick={() => setDetail(a.id)}
              className="card-hover group cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
              <div className="relative h-44 w-full overflow-hidden bg-white/[0.03]">
                {a.cover ? (
                  <img src={a.cover} alt={a.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground"><ImageIcon className="h-8 w-8 opacity-40" /></div>
                )}
                {a.image_count > 1 && (
                  <span className="absolute bottom-2 left-2 flex items-center gap-1 rounded-md bg-black/60 px-2 py-0.5 text-[11px] text-white backdrop-blur">
                    <ImageIcon className="h-3 w-3" /> {a.image_count}
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between">
                  <span className="rounded-md bg-cyan/10 px-2 py-0.5 text-xs text-cyan">{a.section}</span>
                  <span className="text-xs text-muted-foreground">{a.city}</span>
                </div>
                <h3 className="mt-2 font-display font-bold text-white">{a.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{a.description}</p>
                {a.price && <div className="mt-2 text-sm font-semibold text-gold">{a.price}</div>}
                <div className="mt-3 flex items-center justify-between border-t border-white/5 pt-3">
                  <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><Hash className="h-3 w-3 text-cyan" /> <span className="font-en" dir="ltr">{a.aql_code}</span></span>
                  {a.whatsapp && (
                    <a data-testid={`ad-whatsapp-${a.id}`} href={waLink(a.whatsapp, `مرحبًا، بخصوص إعلان: ${a.title}`)} target="_blank" rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1.5 rounded-lg bg-emerald-500/15 px-3 py-1.5 text-xs font-semibold text-emerald-400 transition-colors hover:bg-emerald-500/25">
                      <MessageCircle className="h-3.5 w-3.5" /> واتساب
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {ads.length === 0 && (
          <div className="rounded-2xl border border-dashed border-white/10 py-16 text-center text-muted-foreground">
            لا توجد إعلانات في هذا القسم بعد — كن أول من ينشر!
          </div>
        )}
      </section>

      <div className="h-20" />

      {/* create dialog */}
      <Dialog open={open} onOpenChange={(nextOpen) => { setOpen(nextOpen); if (!nextOpen) { setPaymentOrder(null); setForm(EMPTY); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0b1116] sm:max-w-lg" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right font-display text-white">{paymentOrder ? "رسوم صفحة متجرك" : `إنشاء صفحة إعلان في ${cat?.name}`}</DialogTitle>
            <DialogDescription className="text-right text-muted-foreground">
              {paymentOrder ? "بعد تأكيد التحويل تراجع الإدارة الصفحة قبل نشرها للعامة." : "أنشئ واجهة متجر مصغرة بمنتجاتك؛ رسوم الإدراج 50 ريالًا، والنشر بعد تأكيد الدفع وموافقة الإدارة."}
            </DialogDescription>
          </DialogHeader>
          {paymentOrder ? (
            <div className="space-y-4 rounded-xl border border-gold/20 bg-gold/5 p-4">
              <div className="flex items-center justify-between"><span className="text-sm text-white">رسوم إنشاء صفحة الإعلان</span><strong className="text-2xl text-gold">50 ريال</strong></div>
              <div className="space-y-1 text-sm text-white/80">
                <p>البنك: {paymentOrder.payment.bank_name}</p>
                <p>المستفيد: {paymentOrder.payment.bank_beneficiary}</p>
                <p dir="ltr" className="font-en text-left">IBAN: {paymentOrder.payment.bank_iban}</p>
                <p>رقم الطلب: <span dir="ltr" className="font-en">{paymentOrder.reference}</span></p>
              </div>
              <p className="text-xs leading-6 text-muted-foreground">أرسل إيصال التحويل عبر واتساب مع رقم الطلب. ستظهر الصفحة بعد تأكيد الدفع ومراجعة المحتوى من الإدارة.</p>
              {paymentOrder.payment.whatsapp_number && <a href={waLink(paymentOrder.payment.whatsapp_number, `إيصال رسوم صفحة الإعلان، رقم الطلب ${paymentOrder.reference}، المبلغ 50 ريال`)} target="_blank" rel="noreferrer" className="flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-600 font-semibold text-white"><MessageCircle className="h-4 w-4" /> إرسال الإيصال عبر واتساب</a>}
              <Button onClick={() => { setOpen(false); setPaymentOrder(null); setForm(EMPTY); }} variant="outline" className="w-full border-white/15 bg-transparent text-white">تم، إغلاق</Button>
            </div>
          ) : (
          <>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-white">المنطقة / المدينة</Label>
                <select data-testid="ad-city-select" value={city} onChange={(e) => setCity(e.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white">
                  {cities.map((c) => <option key={c.id} value={c.name} className="bg-[#0b1116]">{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-white">القسم</Label>
                <select data-testid="ad-section-select" value={section || cat?.sections?.[0] || ""} onChange={(e) => setSection(e.target.value)}
                  className="mt-1 h-10 w-full rounded-lg border border-white/10 bg-white/5 px-3 text-sm text-white">
                  {(cat?.sections || []).map((s) => <option key={s} value={s} className="bg-[#0b1116]">{s}</option>)}
                </select>
              </div>
            </div>

            {/* image uploader */}
            <div>
              <Label className="text-xs text-white">الصور (حتى 10) — من الاستوديو أو التصوير</Label>
              <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" onChange={onFiles} className="hidden" data-testid="ad-file-input" />
              <div className="mt-2 grid grid-cols-4 gap-2">
                {form.images.map((src, i) => (
                  <div key={i} className="relative aspect-square overflow-hidden rounded-lg border border-white/10">
                    <img src={src} alt={`img-${i}`} className="h-full w-full object-cover" />
                    <button onClick={() => removeImg(i)} className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 text-white hover:bg-rose-500">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {form.images.length < 10 && (
                  <button data-testid="ad-upload-trigger" onClick={() => fileRef.current?.click()} disabled={uploading}
                    className="flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-white/15 text-muted-foreground transition-colors hover:border-cyan/40 hover:text-cyan">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                    <span className="text-[10px]">إضافة</span>
                  </button>
                )}
              </div>
            </div>

            <div><Label className="text-xs text-white">عنوان الإعلان</Label>
              <Input data-testid="ad-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 border-white/10 bg-white/5 text-right text-white" placeholder="مثال: تويوتا كامري 2022 للبيع" /></div>
            <div><Label className="text-xs text-white">الوصف الكامل</Label>
              <Textarea data-testid="ad-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 border-white/10 bg-white/5 text-right text-white" placeholder="اكتب كل تفاصيل السلعة/الخدمة…" rows={4} /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              {(CATEGORY_FIELDS[id] || []).map((field) => <div key={field.key}><Label className="text-xs text-white">{field.label}</Label><Input value={form.details?.[field.key] || ""} onChange={(e) => setForm({ ...form, details: { ...form.details, [field.key]: e.target.value } })} className="mt-1 border-white/10 bg-white/5 text-right text-white" placeholder={`${field.label}...`} /></div>)}
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="mb-3 flex items-center justify-between"><div><h3 className="text-sm font-semibold text-white">منتجات وخدمات صفحة المتجر</h3><p className="text-xs text-muted-foreground">أضف حتى 20 عنصرًا؛ تظهر كبطاقات في صفحة الإعلان.</p></div><Button type="button" onClick={addProduct} disabled={form.products.length >= 20} variant="outline" className="h-8 gap-1 border-white/15 bg-transparent px-3 text-xs text-white"><Plus className="h-3 w-3" /> منتج</Button></div>
              <div className="space-y-3">
                {form.products.map((product, index) => (
                  <div key={index} className="grid grid-cols-[1fr_auto] gap-2 rounded-lg border border-white/5 p-2">
                    <div className="grid gap-2 sm:grid-cols-[1fr_120px]">
                      <Input value={product.name} onChange={(e) => updateProduct(index, "name", e.target.value)} className="border-white/10 bg-white/5 text-right text-white" placeholder={`اسم المنتج أو الخدمة ${index + 1}`} />
                      <Input value={product.price} onChange={(e) => updateProduct(index, "price", e.target.value)} className="border-white/10 bg-white/5 text-right text-white" placeholder="السعر" />
                      <Textarea value={product.description} onChange={(e) => updateProduct(index, "description", e.target.value)} className="min-h-16 border-white/10 bg-white/5 text-right text-white sm:col-span-2" placeholder="وصف مختصر (اختياري)" />
                    </div>
                    {form.products.length > 1 && <button type="button" onClick={() => removeProduct(index)} aria-label="حذف المنتج" className="self-start p-2 text-rose-300"><X className="h-4 w-4" /></button>}
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white">السعر (اختياري)</Label>
                <Input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 border-white/10 bg-white/5 text-right text-white" placeholder="65,000 ريال" /></div>
              <div><Label className="text-xs text-white">النشاط التجاري (اختياري)</Label>
                <Input value={form.trade} onChange={(e) => setForm({ ...form, trade: e.target.value })} className="mt-1 border-white/10 bg-white/5 text-right text-white" placeholder="معرض / مؤسسة…" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-white">رقم واتساب *</Label>
                <Input data-testid="ad-whatsapp-input" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-1 border-white/10 bg-white/5 text-white" placeholder="05xxxxxxxx" dir="ltr" /></div>
              <div><Label className="text-xs text-white">اسم المعلن (اختياري)</Label>
                <Input value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })} className="mt-1 border-white/10 bg-white/5 text-right text-white" placeholder="اسمك / متجرك" /></div>
            </div>
            <div><Label className="text-xs text-white">البريد الإلكتروني لاستلام تحديث الطلب *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 border-white/10 bg-white/5 text-white" dir="ltr" placeholder="name@example.com" /></div>
            <div><Label className="text-xs text-white">موقع المتجر الحالي (اختياري)</Label><Input value={form.site_url} onChange={(e) => setForm({ ...form, site_url: e.target.value })} className="mt-1 border-white/10 bg-white/5 text-white" dir="ltr" placeholder="https://example.com" /></div>
            <label className="flex items-start gap-2 text-xs leading-5 text-muted-foreground"><input type="checkbox" checked={form.whatsapp_updates_consent} onChange={(e) => setForm({ ...form, whatsapp_updates_consent: e.target.checked })} className="mt-1 accent-cyan" /> أوافق على إرسال تحديثات طلب الإعلان وتعليمات الدفع إلى رقم واتساب الذي أدخلته.</label>
          </div>
          <Button data-testid="submit-ad-btn" onClick={submit} disabled={loading} className="btn-cyan h-11 w-full gap-2 rounded-xl font-semibold hover:bg-transparent">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            إنشاء الطلب ودفع 50 ريالًا
          </Button>
          </>
          )}
        </DialogContent>
      </Dialog>

      <AdDetail adId={detail} onClose={() => setDetail(null)} />
    </main>
  );
}

function AdDetail({ adId, onClose }) {
  const [ad, setAd] = useState(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!adId) { setAd(null); return; }
    setIdx(0); setAd(null);
    api.getAd(adId).then(setAd).catch(() => onClose());
  }, [adId]);
  if (!adId) return null;
  const imgs = ad?.images || [];
  return (
    <Dialog open={!!adId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-white/10 bg-[#0b1116] sm:max-w-2xl" dir="rtl" data-testid="ad-detail-dialog">
        {!ad ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-cyan" /></div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-right font-display text-2xl text-white">{ad.title}</DialogTitle>
              <DialogDescription className="text-right text-muted-foreground">
                {ad.city} · {ad.section}{ad.trade ? ` · ${ad.trade}` : ""}
              </DialogDescription>
            </DialogHeader>

            {imgs.length > 0 ? (
              <div className="relative overflow-hidden rounded-xl border border-white/10 bg-black/30">
                <img src={imgs[idx]} alt={`img-${idx}`} className="max-h-[46vh] w-full object-contain" />
                {imgs.length > 1 && (
                  <>
                    <button onClick={() => setIdx((i) => (i - 1 + imgs.length) % imgs.length)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"><ChevronRight className="h-5 w-5" /></button>
                    <button onClick={() => setIdx((i) => (i + 1) % imgs.length)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"><ChevronLeft className="h-5 w-5" /></button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2 py-0.5 text-xs text-white">{idx + 1}/{imgs.length}</div>
                  </>
                )}
              </div>
            ) : (
              <div className="flex h-48 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-muted-foreground"><ImageIcon className="h-10 w-10 opacity-40" /></div>
            )}

            {imgs.length > 1 && (
              <div className="mt-2 flex gap-2 overflow-x-auto">
                {imgs.map((src, i) => (
                  <button key={i} onClick={() => setIdx(i)} className={`h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 ${i === idx ? "border-cyan" : "border-transparent opacity-60"}`}>
                    <img src={src} alt={`t-${i}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {ad.price && <div className="mt-4 text-xl font-bold text-gold">{ad.price}</div>}
            <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-white/90">{ad.description}</p>

            <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
              {ad.advertiser && <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><Store className="h-4 w-4" /> {ad.advertiser}</span>}
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Hash className="h-3.5 w-3.5 text-cyan" /> <span className="font-en" dir="ltr">{ad.aql_code}</span></span>
            </div>
            {ad.whatsapp && (
              <a data-testid="ad-detail-whatsapp" href={waLink(ad.whatsapp, `مرحبًا، بخصوص إعلان: ${ad.title} (${ad.aql_code})`)} target="_blank" rel="noreferrer"
                className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 font-semibold text-white transition-colors hover:bg-emerald-600">
                <MessageCircle className="h-5 w-5" /> تواصل مع المعلن على واتساب
              </a>
            )}
            <Link to={`/ad/${ad.id}`} className="mt-3 flex h-10 w-full items-center justify-center rounded-lg border border-cyan/25 text-sm font-semibold text-cyan hover:bg-cyan/5">فتح صفحة المتجر الكاملة</Link>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
